import { useCallback, useEffect } from "react";

import { useIsFetching, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";

import ParseEventStreamService from "app/pages/sales-intelligence/modules/ai-agent/services/streamParser/streamParserService";
import type { StreamEvent } from "app/pages/sales-intelligence/modules/ai-agent/services/streamParser/types";
import type { ForceResultWithFullResponse, PostConfigObject } from "app/services/fetchService";

type FetcherFn<Payload = unknown, Dto = unknown> = (
    payload?: Payload,
    options?: Readonly<PostConfigObject>,
) => Promise<ForceResultWithFullResponse<Dto>>;

type UseSSEStreamOptions<Payload = unknown, Dto = unknown> = {
    enabled?: boolean;
    payload?: Payload;
    queryKey: readonly unknown[];
    fetcher: FetcherFn<Payload, Dto>;
    onEvent: (event: StreamEvent) => void;
    onComplete?: VoidFunction;
    queryOptions?: Omit<UseQueryOptions, "queryKey" | "queryFn">;
};

export const useSSEStream = <Payload = unknown, Dto = unknown>({
    enabled = true,
    payload,
    fetcher,
    onEvent,
    queryKey,
    onComplete,
    queryOptions,
}: UseSSEStreamOptions<Payload, Dto>) => {
    const queryClient = useQueryClient();

    const { isLoading, isPending, isFetching, isFetched, isError, refetch, isFetchedAfterMount } = useQuery({
        queryKey,
        queryFn: async ({ signal }) => {
            const { response } = await fetcher(payload, {
                isRawResponse: true,
                withFullResponse: true,
                cancellation: signal,
            });

            if (!response || !response.ok) {
                throw new Error(`Error: ${response?.status} ${response?.statusText}`);
            }

            await ParseEventStreamService.parseEventStream(response, { onEvent });

            return { success: true };
        },
        enabled,
        retry: false,
        staleTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        throwOnError: true,
        ...queryOptions,
    });

    const fetchingCount = useIsFetching({ queryKey, exact: true });
    const isFetchingQuery = fetchingCount > 0;

    useEffect(() => {
        if (!isFetchingQuery && isFetchedAfterMount) {
            onComplete?.();
        }
    }, [isFetchingQuery, isFetchedAfterMount]);

    const stop = useCallback(() => {
        return queryClient.cancelQueries({ queryKey });
    }, [queryKey]);

    return { isLoading, isPending, isFetching, isFetched, isError, stop, refetch, isFetchedAfterMount };
};
